import React, { useState, useEffect } from 'react';
import {
  Building2,
  Stethoscope,
  CreditCard,
  ShoppingCart,
  Factory,
  GraduationCap,
  Scale,
  Home,
  Cpu,
  MessageSquare,
  FileText,
  Database,
  BarChart3,
  PenTool,
  Workflow,
  Shield,
  TrendingUp,
  Zap,
  Brain,
  Users,
  Clock,
  Target,
  CheckCircle,
  ArrowRight,
  Sparkles,
  Bot,
  Network,
  Layers,
  Activity,
  Globe,
  Settings,
  Lightbulb,
  Rocket,
  Star
} from 'lucide-react';

const SolutionsBody = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activeIndustryIndex, setActiveIndustryIndex] = useState(0);
  const [activeSolutionIndex, setActiveSolutionIndex] = useState(0);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndustryIndex((prev) => (prev + 1) % industries.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const industries = [
    {
      icon: <Stethoscope className="w-8 h-8" />,
      name: 'Healthcare',
      description: 'Automate patient intake, medical record analysis, and treatment recommendations with secure AI agents.',
      solutions: ['Patient Documentation', 'Medical Research', 'Appointment Scheduling', 'Insurance Processing'],
      color: 'from-red-500 to-pink-500'
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      name: 'Financial Services',
      description: 'Streamline compliance, risk assessment, and customer service with intelligent automation.',
      solutions: ['Fraud Detection', 'Loan Processing', 'Regulatory Compliance', 'Customer Onboarding'],
      color: 'from-green-500 to-blue-500'
    },
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      name: 'E-commerce',
      description: 'Enhance customer experience with personalized recommendations and automated support.',
      solutions: ['Product Recommendations', 'Inventory Management', 'Customer Support', 'Price Optimization'],
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: <Factory className="w-8 h-8" />,
      name: 'Manufacturing',
      description: 'Optimize production workflows, quality control, and predictive maintenance.',
      solutions: ['Quality Control', 'Predictive Maintenance', 'Supply Chain', 'Safety Monitoring'],
      color: 'from-gray-500 to-blue-500'
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      name: 'Education',
      description: 'Personalize learning experiences and automate administrative tasks.',
      solutions: ['Personalized Learning', 'Student Assessment', 'Administrative Tasks', 'Content Creation'],
      color: 'from-purple-500 to-blue-500'
    },
    {
      icon: <Scale className="w-8 h-8" />,
      name: 'Legal',
      description: 'Accelerate document review, contract analysis, and legal research.',
      solutions: ['Document Review', 'Contract Analysis', 'Legal Research', 'Compliance Monitoring'],
      color: 'from-blue-500 to-purple-500'
    }
  ];

  const useCases = [
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: 'Customer Support Automation',
      description: 'Intelligent agents that handle customer inquiries 24/7 with context from multiple knowledge sources.',
      features: ['Multi-channel support', 'Context-aware responses', 'Escalation handling', 'Sentiment analysis'],
      roi: '60% reduction in response time'
    },
    {
      icon: <FileText className="w-6 h-6" />,
      title: 'Document Processing',
      description: 'Extract, analyze, and process documents using multiple LLMs for maximum accuracy.',
      features: ['OCR integration', 'Data extraction', 'Classification', 'Validation workflows'],
      roi: '80% faster processing'
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: 'Knowledge Management',
      description: 'Connect disparate knowledge sources with RAG-powered intelligent search and retrieval.',
      features: ['Multi-source integration', 'Semantic search', 'Auto-categorization', 'Access controls'],
      roi: '75% faster information retrieval'
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: 'Data Analysis & Insights',
      description: 'Transform raw data into actionable insights using orchestrated AI analysis pipelines.',
      features: ['Automated reporting', 'Trend analysis', 'Predictive modeling', 'Visual dashboards'],
      roi: '90% reduction in analysis time'
    },
    {
      icon: <PenTool className="w-6 h-6" />,
      title: 'Content Generation',
      description: 'Create personalized content at scale across multiple formats and channels.',
      features: ['Multi-format output', 'Brand consistency', 'A/B testing', 'Quality assurance'],
      roi: '70% increase in content velocity'
    },
    {
      icon: <Workflow className="w-6 h-6" />,
      title: 'Workflow Orchestration',
      description: 'Automate complex business processes with intelligent decision-making capabilities.',
      features: ['Process automation', 'Decision trees', 'Exception handling', 'Performance monitoring'],
      roi: '85% process efficiency gain'
    }
  ];

  const solutionTemplates = [
    {
      name: 'E-commerce Support Bot',
      description: 'Complete customer service automation for online stores',
      components: ['ChatGPT for conversations', 'RAG for product knowledge', 'Workflow for order processing'],
      timeToValue: '2 days'
    },
    {
      name: 'Document Intelligence Suite',
      description: 'Extract and analyze insights from business documents',
      components: ['Claude for analysis', 'Multiple LLMs for validation', 'Custom knowledge base'],
      timeToValue: '1 week'
    },
    {
      name: 'HR Onboarding Assistant',
      description: 'Streamline new employee onboarding process',
      components: ['Multi-LLM processing', 'Document workflows', 'Compliance checking'],
      timeToValue: '3 days'
    },
    {
      name: 'Financial Report Analyzer',
      description: 'Automated analysis of financial documents and reports',
      components: ['Specialized financial LLMs', 'Risk assessment', 'Compliance validation'],
      timeToValue: '1 week'
    }
  ];

  const integrations = [
    { name: 'Slack', category: 'Communication' },
    { name: 'Microsoft Teams', category: 'Communication' },
    { name: 'Salesforce', category: 'CRM' },
    { name: 'HubSpot', category: 'CRM' },
    { name: 'Shopify', category: 'E-commerce' },
    { name: 'Zendesk', category: 'Support' },
    { name: 'Jira', category: 'Project Management' },
    { name: 'Confluence', category: 'Knowledge' },
    { name: 'Google Workspace', category: 'Productivity' },
    { name: 'Microsoft 365', category: 'Productivity' },
    { name: 'AWS', category: 'Cloud' },
    { name: 'Azure', category: 'Cloud' }
  ];

  const successMetrics = [
    { metric: '85%', label: 'Average Cost Reduction', icon: <TrendingUp className="w-5 h-5" /> },
    { metric: '92%', label: 'Customer Satisfaction', icon: <Users className="w-5 h-5" /> },
    { metric: '70%', label: 'Time Savings', icon: <Clock className="w-5 h-5" /> },
    { metric: '3x', label: 'Productivity Increase', icon: <Rocket className="w-5 h-5" /> }
  ];

  return (
    <main className="bg-black text-white font-sans">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(59, 130, 246, 0.15), transparent 40%)`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-transparent to-blue-900/20" />
      </div>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-full px-4 py-2 mb-6">
              <Lightbulb className="w-4 h-4 text-purple-400" />
              <span className="text-sm text-purple-300">Solutions for Every Industry</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white via-purple-200 to-blue-200 bg-clip-text text-transparent">
              Transform Your Business
              <span className="block mt-2">with AI Solutions</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-12">
              Discover how SelficAI's orchestration platform solves real business challenges across industries. 
              From customer service automation to complex document processing, our AI agents adapt to your unique needs.
            </p>

            {/* Success Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {successMetrics.map((item, index) => (
                <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 text-center">
                  <div className="text-purple-400 mb-2 flex justify-center">
                    {item.icon}
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-white mb-1">{item.metric}</div>
                  <div className="text-sm text-gray-400">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Industry-Specific Solutions
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Tailored AI orchestration solutions designed for the unique challenges of your industry.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <div 
                key={index} 
                className={`group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-3xl p-8 hover:border-purple-500/50 transition-all duration-300 cursor-pointer ${
                  activeIndustryIndex === index ? 'scale-105 border-purple-500/50' : ''
                }`}
                onMouseEnter={() => setActiveIndustryIndex(index)}
              >
                <div className={`text-transparent bg-gradient-to-r ${industry.color} bg-clip-text mb-4`}>
                  {industry.icon}
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{industry.name}</h3>
                <p className="text-gray-300 mb-6 leading-relaxed">{industry.description}</p>
                <div className="space-y-2">
                  {industry.solutions.map((solution, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <div className="w-1.5 h-1.5 bg-purple-400 rounded-full flex-shrink-0" />
                      <span className="text-sm text-gray-400">{solution}</span>
                    </div>
                  ))}
                </div>
                <button className="mt-6 flex items-center space-x-2 text-purple-400 hover:text-purple-300 transition-colors group-hover:translate-x-1">
                  <span className="text-sm font-medium">Learn More</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Grid */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Powerful Use Cases
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From simple automation to complex orchestration, see how our platform tackles diverse business challenges.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-6 hover:border-purple-500/50 transition-all duration-300 hover:scale-105">
                <div className="text-purple-400 mb-4">
                  {useCase.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{useCase.title}</h3>
                <p className="text-gray-300 mb-4 text-sm leading-relaxed">{useCase.description}</p>
                
                <div className="space-y-2 mb-4">
                  {useCase.features.map((feature, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                      <span className="text-xs text-gray-400">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 border border-purple-500/30 rounded-lg p-3">
                  <div className="text-purple-300 text-xs font-medium">Expected ROI</div>
                  <div className="text-white font-semibold">{useCase.roi}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution Templates */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Ready-to-Deploy Templates
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Pre-built solution templates that you can customize and deploy in minutes, not months.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {solutionTemplates.map((template, index) => (
              <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-2xl p-8 hover:border-purple-500/50 transition-all duration-300">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{template.name}</h3>
                    <p className="text-gray-300 text-sm">{template.description}</p>
                  </div>
                  <div className="bg-green-500/20 text-green-300 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-4">
                    {template.timeToValue}
                  </div>
                </div>
                
                <div className="space-y-3 mb-6">
                  <h4 className="text-sm font-semibold text-purple-300">Included Components:</h4>
                  {template.components.map((component, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <Bot className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      <span className="text-sm text-gray-400">{component}</span>
                    </div>
                  ))}
                </div>

                <button className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 px-6 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-2">
                  <span>Deploy Template</span>
                  <Rocket className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Seamless Integrations
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Connect with your existing tools and platforms for a unified AI-powered workflow.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {integrations.map((integration, index) => (
              <div key={index} className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-white/20 rounded-xl p-4 text-center hover:border-purple-500/50 transition-all duration-300 hover:scale-105">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg mx-auto mb-3 flex items-center justify-center">
                  <Network className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-white text-sm mb-1">{integration.name}</h3>
                <p className="text-xs text-gray-400">{integration.category}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-400 mb-4">Don't see your tool? We integrate with 500+ platforms via API.</p>
            <button className="border border-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
              View All Integrations
            </button>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 backdrop-blur-xl border border-purple-500/30 rounded-3xl p-12">
            <div className="text-center mb-8">
              <BarChart3 className="w-12 h-12 text-purple-400 mx-auto mb-4" />
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
                Calculate Your ROI
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed">
                See how much time and money you can save with SelficAI's intelligent automation.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 rounded-xl p-6 text-center">
                <div className="text-2xl font-bold text-white mb-2">Average Savings</div>
                <div className="text-3xl font-bold text-green-400 mb-2">$50K+</div>
                <div className="text-sm text-gray-400">per month</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6 text-center">
                <div className="text-2xl font-bold text-white mb-2">Time Reduction</div>
                <div className="text-3xl font-bold text-blue-400 mb-2">70%</div>
                <div className="text-sm text-gray-400">faster processes</div>
              </div>
              <div className="bg-white/10 rounded-xl p-6 text-center">
                <div className="text-2xl font-bold text-white mb-2">Payback Period</div>
                <div className="text-3xl font-bold text-purple-400 mb-2">3-6</div>
                <div className="text-sm text-gray-400">months</div>
              </div>
            </div>

            <div className="text-center">
              <button className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-2 mx-auto">
                <span>Get Custom ROI Report</span>
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-purple-600/20 to-blue-600/20 backdrop-blur-xl border border-purple-500/30 rounded-3xl p-12">
            <Rocket className="w-12 h-12 text-purple-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Join thousands of companies already automating their workflows with SelficAI. 
              Start with a template or build custom solutions for your unique needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-blue-700 transition-all duration-300 flex items-center justify-center space-x-2">
                <span>Start Free Trial</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <button className="border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                Schedule Demo
              </button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default SolutionsBody;